import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/Header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const location = useLocation();
  const aboutDropdownRef = useRef(null);

  const projectPortalUrl =
    process.env.REACT_APP_PROJECT_PORTAL_URL ||
    'https://causeandimpact--mvp.sandbox.my.site.com/Portal/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
    setIsAboutOpen(false);
    document.body.classList.remove('menu-open');
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isAboutOpen &&
        aboutDropdownRef.current &&
        !aboutDropdownRef.current.contains(event.target)
      ) {
        setIsAboutOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAboutOpen]);

  useEffect(() => {
    if (
      !document.querySelector(
        'script[src="https://assets.calendly.com/assets/external/widget.js"]'
      )
    ) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
    setIsAboutOpen(false);
    document.body.classList.remove('menu-open');
  };

  const handleMobileMenuToggle = () => {
    const nextOpen = !isMobileMenuOpen;
    setIsMobileMenuOpen(nextOpen);
    document.body.classList.toggle('menu-open', nextOpen);

    if (!nextOpen) {
      setIsAboutOpen(false);
    }
  };

  const handleAboutToggle = () => {
    setIsAboutOpen((prev) => !prev);
  };

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: 'https://calendly.com/leighton-jacobus',
      });
    }
    handleNavClick();
  };

  const NavLink = ({ to, children, className = '', role }) => (
    <Link
      to={to}
      onClick={handleNavClick}
      role={role}
      className={`${className} ${location.pathname === to ? 'active' : ''}`.trim()}
    >
      {children}
    </Link>
  );

  const isAboutActive =
    location.pathname.startsWith('/our-story') ||
    location.pathname.startsWith('/leadership');

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <Link to="/" className="logo" onClick={handleNavClick}>
          <img
            src="/CandIWhiteLogo.svg"
            alt="Cause and Impact Logo"
            className="logo-image"
          />
          <h1>
            <span className="logo-text">Cause</span>
            <span className="logo-separator-wrapper">
              <span className="logo-separator">&</span>
            </span>
            <span className="logo-text">Impact</span>
          </h1>
        </Link>

        <button
          className={`mobile-menu-button ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={handleMobileMenuToggle}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="main-navigation"
          type="button"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <nav
          id="main-navigation"
          className={`main-nav ${isMobileMenuOpen ? 'open' : ''}`}
        >
          <ul>
            <li>
              <NavLink to="/">Home</NavLink>
            </li>

            <li
              className={`about-dropdown ${isAboutOpen ? 'open' : ''}`}
              onMouseEnter={() => {
                if (window.innerWidth > 1280) setIsAboutOpen(true);
              }}
              onMouseLeave={() => {
                if (window.innerWidth > 1280) setIsAboutOpen(false);
              }}
              ref={aboutDropdownRef}
            >
              <button
                className={`about-trigger ${isAboutActive ? 'active' : ''}`}
                onClick={handleAboutToggle}
                aria-haspopup="true"
                aria-expanded={isAboutOpen}
                aria-controls="about-menu"
                type="button"
              >
                About Us
              </button>

              <ul id="about-menu" className="about-menu" role="menu">
                <li>
                  <NavLink to="/our-story" role="menuitem">
                    Our Story
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/leadership" role="menuitem">
                    Our Team
                  </NavLink>
                </li>
              </ul>
            </li>

            <li>
              <NavLink to="/services">Services</NavLink>
            </li>

            <li>
              <NavLink to="/transparency">Transparency</NavLink>
            </li>

            <li>
              <NavLink to="/cost-calculator" className="cost-calculator-button">
                501(c)(3) Cost Calculator
              </NavLink>
            </li>

            <li>
              <NavLink to="/causes" className="causes-button">
                Causes
              </NavLink>
            </li>

            <li>
              <NavLink to="/academy" className="academy-button">
                Impact Academy
              </NavLink>
            </li>

            <li>
              <a
                href={projectPortalUrl}
                className="portal-button"
                onClick={handleNavClick}
              >
                Cause Portal
              </a>
            </li>

            <li>
              <button
                onClick={openCalendly}
                className="schedule-call-button"
                type="button"
              >
                Talk to Our Team
              </button>
            </li>

            <li>
              <NavLink to="/donate" className="donate-button">
                Donate
              </NavLink>
            </li>

            <li>
              <NavLink to="/apply" className="apply-button">
                Apply Now
              </NavLink>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;